/**
 * Expenses Tab Page
 * Groups Overview, Common Expenses, Subscriptions, and Loans & Mortgages.
 * Overview shows consolidated expense totals across all sources.
 */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayoutDashboard, Receipt, Repeat, CreditCard, Landmark } from 'lucide-react';
import { ExpenseOverview } from './expense-overview';
import { ExpensesPage } from './expenses-page';
import { SubscriptionsPage } from './subscriptions-page';
import { ChargeAccountsPage } from './charge-accounts-page';
import { LoansPage } from './loans-page';

const TAB_ROUTES: Record<string, string> = {
  overview: '/billing/expenses',
  expenses: '/billing/expenses',
  subscriptions: '/billing/subscriptions',
  'charge-accounts': '/billing/charge-accounts',
  loans: '/billing/loans',
};

function resolveTab(pathname: string): string {
  if (pathname.endsWith('/subscriptions')) return 'subscriptions';
  if (pathname.endsWith('/charge-accounts')) return 'charge-accounts';
  if (pathname.endsWith('/loans')) return 'loans';
  return 'overview';
}

export function ExpensesTabsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => resolveTab(location.pathname));

  function handleTabChange(value: string) {
    setTab(value);
    if (value !== tab) {
      navigate(TAB_ROUTES[value] || '/billing/expenses', { replace: true });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Expenses</h1>
        <p className="text-muted-foreground">Track expenses, subscriptions, loans, and monitor outgoing costs</p>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <LayoutDashboard className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="expenses" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Common Expenses
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="flex items-center gap-2">
            <Repeat className="h-4 w-4" />
            Subscriptions
          </TabsTrigger>
          <TabsTrigger value="charge-accounts" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Charge Accounts
          </TabsTrigger>
          <TabsTrigger value="loans" className="flex items-center gap-2">
            <Landmark className="h-4 w-4" />
            Loans & Mortgages
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <ExpenseOverview />
        </TabsContent>

        <TabsContent value="expenses">
          <ExpensesPage />
        </TabsContent>

        <TabsContent value="subscriptions">
          <SubscriptionsPage />
        </TabsContent>

        <TabsContent value="charge-accounts">
          <ChargeAccountsPage />
        </TabsContent>

        <TabsContent value="loans">
          <LoansPage />
        </TabsContent>
      </Tabs>
    </div>
  );
}
